import { CompanyForSearchTable } from 'pages/find-new/prospect/companies/find-new-prospect-companies.types';
import { ContactForSearchTable } from 'pages/find-new/prospect/contacts/find-new-prospect-contacts.types';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useCustomLocation } from 'context/customLocation';
import {
  CronoGatewayLinkedinSearchLeadsAction,
  SalesNavSearchCompaniesFilter,
  SalesNavSearchLeadsFilter,
} from 'crono-fe-common/types/crono-extension/linkedin';
import { FindNewDatabase } from 'pages/find-new/components/FindNewDatabaseSelect';
import { readStoredTab, TAB_STORAGE_KEY } from 'pages/find-new/findNewTab';

interface FindNewTabContextType {
  companiesList: CompanyForSearchTable[];
  setCompaniesList: React.Dispatch<
    React.SetStateAction<CompanyForSearchTable[]>
  >;
  countCompaniesList: number | null;
  setCountCompaniesList: React.Dispatch<React.SetStateAction<number | null>>;
  totalCompaniesList: number | null;
  setTotalCompaniesList: React.Dispatch<React.SetStateAction<number | null>>;
  initialPageToLoadCompaniesList: number | null;
  setInitialPageToLoadCompaniesList: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  nextPageToLoadCompaniesList: number | null;
  setNextPageToLoadCompaniesList: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  filtersUserCompaniesList:
    | (SalesNavSearchCompaniesFilter & {
        keywords?: string | null;
        start?: number | null;
        count?: number | null;
      })
    | null;
  setFiltersUserCompaniesList: React.Dispatch<
    React.SetStateAction<
      | (SalesNavSearchCompaniesFilter & {
          keywords?: string | null;
          start?: number | null;
          count?: number | null;
        })
      | null
    >
  >;
  contactsList: ContactForSearchTable[];
  setContactsList: React.Dispatch<
    React.SetStateAction<ContactForSearchTable[]>
  >;
  countContactsList: number | null;
  setCountContactsList: React.Dispatch<React.SetStateAction<number | null>>;
  totalContactsList: number | null;
  setTotalContactsList: React.Dispatch<React.SetStateAction<number | null>>;
  initialPageToLoadContactsList: number | null;
  setInitialPageToLoadContactsList: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  nextPageToLoadContactsList: number | null;
  setNextPageToLoadContactsList: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  filtersUserContactsList:
    | SalesNavSearchLeadsFilter
    | CronoGatewayLinkedinSearchLeadsAction['params']
    | null;
  setFiltersUserContactsList: React.Dispatch<
    React.SetStateAction<
      | SalesNavSearchLeadsFilter
      | CronoGatewayLinkedinSearchLeadsAction['params']
      | null
    >
  >;
  hideExistingCompanies: boolean;
  setHideExistingCompanies: React.Dispatch<React.SetStateAction<boolean>>;
  hideExistingContacts: boolean;
  setHideExistingContacts: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: 'company' | 'contact';
  setActiveTab: React.Dispatch<React.SetStateAction<'company' | 'contact'>>;
  // The database the visible Find New table is currently reading from. Only the
  // Sales Navigator tables publish their rows into this context, so consumers
  // outside the page (e.g. AI-chat tools) need this to know whether those lists
  // reflect what the user actually sees.
  activeDatabase: FindNewDatabase;
  setActiveDatabase: React.Dispatch<React.SetStateAction<FindNewDatabase>>;
}

export const FindNewTabContext = createContext<
  FindNewTabContextType | undefined
>(undefined);

FindNewTabContext.displayName = 'FindNewTabContext';

const FindNewTabProvider: FC<PropsWithChildren> = ({ children }) => {
  const [companiesList, setCompaniesList] = useState<CompanyForSearchTable[]>(
    [],
  );
  const [countCompaniesList, setCountCompaniesList] = useState<number | null>(
    null,
  );
  const [totalCompaniesList, setTotalCompaniesList] = useState<number | null>(
    null,
  );
  const [initialPageToLoadCompaniesList, setInitialPageToLoadCompaniesList] =
    useState<number | null>(null);
  const [nextPageToLoadCompaniesList, setNextPageToLoadCompaniesList] =
    useState<number | null>(null);
  const [filtersUserCompaniesList, setFiltersUserCompaniesList] = useState<
    | (SalesNavSearchCompaniesFilter & {
        keywords?: string | null;
        start?: number | null;
        count?: number | null;
      })
    | null
  >(null);

  const [contactsList, setContactsList] = useState<ContactForSearchTable[]>([]);
  const [countContactsList, setCountContactsList] = useState<number | null>(
    null,
  );
  const [totalContactsList, setTotalContactsList] = useState<number | null>(
    null,
  );
  const [initialPageToLoadContactsList, setInitialPageToLoadContactsList] =
    useState<number | null>(null);
  const [nextPageToLoadContactsList, setNextPageToLoadContactsList] = useState<
    number | null
  >(null);
  const [filtersUserContactsList, setFiltersUserContactsList] = useState<
    | SalesNavSearchLeadsFilter
    | CronoGatewayLinkedinSearchLeadsAction['params']
    | null
  >(null);

  const [hideExistingCompanies, setHideExistingCompanies] = useState(false);
  const [hideExistingContacts, setHideExistingContacts] = useState(false);
  const [activeDatabase, setActiveDatabase] =
    useState<FindNewDatabase>('salesNav');

  const { customStateLocation, setCustomStateLocation } = useCustomLocation();
  const [activeTab, setActiveTab] = useState<'company' | 'contact'>(
    readStoredTab,
  );
  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (customStateLocation?.initialTabFindNew) {
      setActiveTab(customStateLocation?.initialTabFindNew);
      setCustomStateLocation((prev) => ({
        ...prev,
        initialTabFindNew: undefined,
      }));
    }
  }, [
    customStateLocation?.initialTabFindNew,
    setActiveTab,
    setCustomStateLocation,
  ]);

  return (
    <FindNewTabContext.Provider
      value={{
        companiesList,
        setCompaniesList,
        countCompaniesList,
        setCountCompaniesList,
        totalCompaniesList,
        setTotalCompaniesList,
        initialPageToLoadCompaniesList,
        setInitialPageToLoadCompaniesList,
        nextPageToLoadCompaniesList,
        setNextPageToLoadCompaniesList,
        filtersUserCompaniesList,
        setFiltersUserCompaniesList,
        contactsList,
        setContactsList,
        countContactsList,
        setCountContactsList,
        totalContactsList,
        setTotalContactsList,
        nextPageToLoadContactsList,
        setNextPageToLoadContactsList,
        initialPageToLoadContactsList,
        setInitialPageToLoadContactsList,
        filtersUserContactsList,
        setFiltersUserContactsList,
        hideExistingCompanies,
        setHideExistingCompanies,
        hideExistingContacts,
        setHideExistingContacts,
        activeTab,
        setActiveTab,
        activeDatabase,
        setActiveDatabase,
      }}
    >
      {children}
    </FindNewTabContext.Provider>
  );
};

export default FindNewTabProvider;

export function useFindNewTabContext() {
  const context = useContext(FindNewTabContext);
  if (context === undefined) {
    throw new Error(
      `useFindNewTabContext must be used within a FindNewTabProvider`,
    );
  }

  return context;
}
